import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, withModifiers as _withModifiers, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, Fragment as _Fragment, renderList as _renderList, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-3c963179"),n=n(),_popScopeId(),n)
const _hoisted_1 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("hr", null, null, -1))
const _hoisted_2 = {
  key: 0,
  class: "service-group-item__initiate-footer"
}
const _hoisted_3 = {
  key: 1,
  class: "service-group-item__alerts"
}

import { computed, PropType, ref, watch } from 'vue'
import { PublicServiceGroupRelation } from '@/models/service/serviceGroup.model'
import { ValidationErrorsForOneObject } from '@/models/error.model'
import { ServiceType } from '@/enums/services'
import {
  servicePublicDetailsCommonProps,
  serviceResultCardCommonProps,
} from '@/enums/public'
import {
  PeriodAvailability,
  PublicServiceDetails,
} from '@/models/public/service.model'
import EventResultCard from '@/components/public/EventResultCard.vue'
import MoneyJarResultCard from '@/components/public/MoneyJarResultCard.vue'
import ExperienceResultCard from '@/components/public/ExperienceResultCard.vue'
import ProductResultCard from '@/components/public/ProductResultCard.vue'
import ServiceItemCard from '@/components/service/ServiceItemCard.vue'
import GroupResultCard from '@/components/public/group/GroupedGroupResultCard.vue'
import { ALERT_TYPES, CATEGORIES, FORMATS, SIZES } from '@/enums/components'
import BaseButton from '@/components/BaseButton.vue'
import HousingResultCard from '@/components/public/housing/HousingResultCard.vue'
import {
  GroupAvailabilityItem,
  HousingAvailabilityPayload,
  IGroupAvailabilitySearch,
} from '@/models/public/order.model'
import { Alert } from '@/models/shared.model'
import { useI18n } from 'vue-i18n'
import TicketResultCard from '@/components/public/ticket/TicketResultCard.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'PublicServiceGroupItem',
  props: {
  isPreview: servicePublicDetailsCommonProps.isPreview,
  enableOrdering: servicePublicDetailsCommonProps.enableOrdering,
  referral: servicePublicDetailsCommonProps.referral,
  groupService: serviceResultCardCommonProps.groupService,
  payload: serviceResultCardCommonProps.payload,
  value: {
    type: Object as PropType<PublicServiceGroupRelation>,
    required: true,
  },
  errors: {
    type: Object as PropType<ValidationErrorsForOneObject | undefined>,
    default: undefined,
  },
  availabilitySearch: {
    type: Object as PropType<IGroupAvailabilitySearch | undefined>,
    default: undefined,
  },
  availabilityItem: {
    type: Object as PropType<GroupAvailabilityItem | undefined>,
    default: undefined,
  },
},
  emits: ["update:payload"],
  setup(__props, { emit: emits }: { emit: ({
  (e: 'update:payload', newPayload: serviceResultCardCommonProps.payload): void
}), expose: any, slots: any, attrs: any }) {

const props = __props





const i18n = useI18n()

const isSelectionMode = ref<boolean>(false)
/**
 * Should form be shown to user so that they can add item to group order.
 * If item is required, it should be shown right away.
 * If not, user should manually initiate.
 */
const isShowForm = ref<boolean>(!!props.value?.is_required)
const isPriceHidden = ref<boolean>(!!props.value?.is_price_hidden)
const groupedService = computed<PublicServiceGroupRelation>(() => props.value)
const service = computed<PublicServiceDetails>(
  () => groupedService.value.service,
)

/**
 * Does it require manual input to start adding item (show result card form)
 * Differs for required and not required group items
 */
const isUserInitiationRequired = computed<boolean>(() => {
  return !isSelectionMode.value && !props.value?.is_required
})

const hasErrors = computed<boolean>(() => {
  if (!props.errors) {
    return false
  }
  return Object.values(props.errors).length !== 0
})

const alerts = computed<Alert[]>(() => {
  const alertList = []

  if (isUnavailable.value) {
    alertList.push({
      text: i18n.t('serviceGroup.groupItemIsUnavailableForSelection'),
      alertType: ALERT_TYPES.WARNING,
    })
  }

  const nonFieldErrors = (props.errors?.non_field_errors || []) as Array<string>
  for (const errorMessage of nonFieldErrors) {
    alertList.push({
      text: errorMessage,
      alertType: ALERT_TYPES.DANGER,
    })
  }

  return alertList
})

const onPayloadUpdated = (payload) => {
  isSelectionMode.value = payload !== null
  if (payload === null) {
    isShowForm.value = !isUserInitiationRequired.value
  }
  emits('update:payload', payload)
}

const isAvailable = computed<boolean>(() => {
  return props.availabilityItem?.is_available === true
})

/**
 * No availability found based on a search.
 * Different from unknown availability which is represented by undefined value
 */
const isUnavailable = computed<boolean>(() => {
  return props.availabilityItem?.is_available === false
})

const availabilityRequest = computed<HousingAvailabilityPayload | undefined>(
  () => {
    if (service.value.type !== ServiceType.HOUSING) {
      return undefined
    }

    if (!isAvailable.value) {
      return undefined
    }

    const availability: PeriodAvailability =
      props.availabilityItem?.availability
    return {
      check_in: availability.period.start,
      check_out: availability.period.end,
      adults: props.availabilitySearch?.group_size ?? 1,
      children: 0,
    }
  },
)

const isExperience = computed(() => {
  return service.value.type === ServiceType.EXPERIENCE
})

const isEvent = computed(() => {
  return service.value.type === ServiceType.EVENT
})

const isProduct = computed(() => {
  return service.value.type === ServiceType.PRODUCT
})

const selectedDate = computed<number | undefined>(() => {
  if (!isExperience.value && !isEvent.value && !isProduct.value) {
    return undefined
  }

  if (isAvailable.value) {
    if (isEvent.value || isProduct.value) {
      return new Date(props.availabilityItem.availability)
    } else if (isExperience.value) {
      return new Date(props.availabilityItem.availability.date)
    }
    console.warn('Unhandled sales article type')
  }
  return undefined
})

const selectedGroupSize = computed<number | undefined>(() => {
  if (!isExperience.value && !isProduct.value) {
    return undefined
  }
  const groupSize = props.availabilitySearch?.group_size
  return groupSize || undefined // 0 -> undefined
})

watch(
  () => props.availabilityItem,
  () => {
    if (!props.availabilityItem) {
      return
    }
  },
  { immediate: true },
)

watch(
  () => props.payload,
  () => {
    isSelectionMode.value = props.payload !== null
  },
  { immediate: true },
)

watch(
  isSelectionMode,
  () => {
    if (!isSelectionMode.value && isUserInitiationRequired.value) {
      isShowForm.value = false
    }
  },
  {
    immediate: true,
  },
)

return (_ctx: any,_cache: any) => {
  const _component_AlertBanner = _resolveComponent("AlertBanner")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["service-group-item", { 'service-group-item--errors': _unref(hasErrors) }])
  }, [
    _createVNode(ServiceItemCard, {
      grouped: "",
      service: _unref(service),
      required: __props.value.is_required,
      referral: __props.referral,
      class: "service-group-item__header"
    }, null, 8, ["service", "required", "referral"]),
    (__props.enableOrdering)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
          _hoisted_1,
          (!isShowForm.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
                (__props.payload === null)
                  ? (_openBlock(), _createBlock(BaseButton, {
                      key: 0,
                      format: _unref(FORMATS).SECONDARY,
                      size: _unref(SIZES).SMALL,
                      label: _ctx.$t('add'),
                      class: "service-group-item__add-button",
                      onClick: _cache[0] || (_cache[0] = _withModifiers(() => (isShowForm.value = true), ["prevent"]))
                    }, null, 8, ["format", "size", "label"]))
                  : _createCommentVNode("", true)
              ]))
            : (_openBlock(), _createBlock(_resolveDynamicComponent(
          {
            [_unref(ServiceType).PRODUCT]: ProductResultCard,
            [_unref(ServiceType).MONEYJAR]: MoneyJarResultCard,
            [_unref(ServiceType).EVENT]: EventResultCard,
            [_unref(ServiceType).EXPERIENCE]: ExperienceResultCard,
            [_unref(ServiceType).HOUSING]: HousingResultCard,
            [_unref(ServiceType).GROUP]: GroupResultCard,
            [_unref(ServiceType).TICKET]: TicketResultCard,
          }[_unref(service).type] ?? ProductResultCard
        ), {
                key: 1,
                "hide-header": "",
                "show-dates-select": "",
                "service-id": __props.isPreview ? _unref(service).id : _unref(service).hash_id,
                service: _unref(service),
                "group-service": __props.groupService,
                "is-preview": __props.isPreview,
                referral: __props.referral,
                "availability-request": _unref(availabilityRequest),
                "selected-date": _unref(selectedDate),
                "selected-group-size": _unref(selectedGroupSize),
                "show-selected-mode": isSelectionMode.value,
                "availability-search": __props.availabilitySearch,
                "availability-item": __props.availabilityItem,
                payload: __props.payload,
                "is-price-hidden": isPriceHidden.value,
                "onUpdate:payload": _cache[3] || (_cache[3] = (payload) => _ctx.$emit('update:payload', payload))
              }, {
                addButton: _withCtx(({ contentSelected, payload: templatePayload }) => [
                  (!isSelectionMode.value && _unref(isUserInitiationRequired))
                    ? (_openBlock(), _createBlock(BaseButton, {
                        key: 0,
                        format: _unref(FORMATS).TERTIARY,
                        size: _unref(SIZES).SMALL,
                        label: _ctx.$t('cancel'),
                        class: "service-group-item__add-button",
                        onClick: _cache[1] || (_cache[1] = _withModifiers(() => onPayloadUpdated(null), ["prevent"]))
                      }, null, 8, ["format", "size", "label"]))
                    : _createCommentVNode("", true),
                  (__props.payload === null)
                    ? (_openBlock(), _createBlock(BaseButton, {
                        key: 1,
                        format: _unref(FORMATS).SECONDARY,
                        size: _unref(SIZES).SMALL,
                        label: _ctx.$t('confirm'),
                        class: "service-group-item__add-button",
                        disabled: !contentSelected,
                        onClick: _withModifiers(() => onPayloadUpdated(templatePayload), ["prevent"])
                      }, null, 8, ["format", "size", "label", "disabled", "onClick"]))
                    : (_openBlock(), _createBlock(BaseButton, {
                        key: 2,
                        format: _unref(FORMATS).SECONDARY,
                        size: _unref(SIZES).SMALL,
                        category: _unref(CATEGORIES).DELETE,
                        label: _ctx.$t('remove'),
                        class: "service-group-item__remove-button",
                        onClick: _cache[2] || (_cache[2] = _withModifiers(() => onPayloadUpdated(null), ["prevent"]))
                      }, null, 8, ["format", "size", "category", "label"]))
                ]),
                _: 1
              }, 40, ["service-id", "service", "group-service", "is-preview", "referral", "availability-request", "selected-date", "selected-group-size", "show-selected-mode", "availability-search", "availability-item", "payload", "is-price-hidden"]))
        ], 64))
      : _createCommentVNode("", true),
    (_unref(alerts))
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(alerts), (alert, alertIndex) => {
            return (_openBlock(), _createBlock(_component_AlertBanner, {
              key: alertIndex,
              text: alert.text,
              "alert-type": alert.alertType,
              class: "service-group-item__alert"
            }, null, 8, ["text", "alert-type"]))
          }), 128))
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})